import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LineOutlined } from "@ant-design/icons";
import "./MapLayersDrawer.less";
import { Flex } from "antd";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import satelliteIcon from "../../../assets/images/autozones/satellite_alt.svg";
import agriIcon from "../../../assets/images/autozones/agriculture.svg";
import mapsIcon from "../../../assets/images/autozones/add-map.svg";
import AutozonesComponent from "../../autozones";
import MachineryButtonComponent from "../../../shared/mfe/MachineryButton";
import { useNewTrialState } from "../hooks/NewTrialStateProvider";
import SoilButtonComponent from "../../../shared/sl/SoilButton";
import { ReactComponent as ExpandIcon } from "../../../assets/images/open_in_full.svg";
import { ReactComponent as MapLayerMenu } from "../../../assets/images/map-drawer-menu.svg";
import { ReactComponent as NavLayerBtn } from "../../../assets/images/btn-nav-layers.svg";
import {
  AUTOZONE_REQUEST_STATUS,
  AutozoneColorPalette,
} from "../../autozones/types";
import { SoilColorPalette } from "../../../shared/sl/soilMapAnalysisTypes";
import { removeAutoZonesLayer } from "../../../maps/utils/autozones";
import { removeSoilImageLayer } from "../../../maps/utils/soilData";
import { removeFieldOperationsLayer } from "../../../maps/utils/fieldOperations";

// Types
type OverlayType = "layers" | "satellite" | "agriculture" | "map";
type LayerSubtype = "subtype1" | "subtype2";
type LayerSoilL = "subtype1" | "subtype2";

export enum LayerOption {
  Soil = "soil",
  Autozone = "autozone",
  Machinery = "machinery",
}

export type DrawerState = LayerOption | null;

interface IMapLayerDrawer {
  steps?: number;
  mapInstance?: mapboxgl.Map;
}

const MapLayersDrawer: React.FC<IMapLayerDrawer> = ({
  steps,
  mapInstance,
}: IMapLayerDrawer) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const [activeOverlay, setActiveOverlay] = useState<OverlayType | null>(
    "layers"
  );
  const [selectedSubtypes, setSelectedSubtypes] = useState<LayerSubtype[]>([]);
  const {
    autozoneRequestStatus,
    setAutozoneOpacity,
    setAutozoneRequestStatus,
    setSoilOperationsData,
    setAutozonesData,
    setSoilColorPalette,
    setAutozoneColorPalette,
    setFieldOperationsData,
  } = useNewTrialState();

  const [selectedLayer, setSelectedLayer] = useState<DrawerState>(null);

  const toggleDrawerLayer = (layer: LayerOption): void => {
    if (layer !== LayerOption.Soil) {
      setSoilOperationsData(null);
      setSoilColorPalette(SoilColorPalette.DEFAULT);
      mapInstance && removeSoilImageLayer(mapInstance);
    }
    if (layer !== LayerOption.Autozone) {
      setAutozonesData(null);
      mapInstance && removeAutoZonesLayer(mapInstance);
    }
    if (layer !== LayerOption.Machinery) {
      setFieldOperationsData(null);
      mapInstance && removeFieldOperationsLayer(mapInstance);
    }
    setAutozoneRequestStatus(AUTOZONE_REQUEST_STATUS.DEFAULT);
    setSelectedLayer(layer);
    setAutozoneColorPalette(AutozoneColorPalette.DEFAULT);
  };

  const hideDrawer = (): void => {
    setAutozoneRequestStatus(AUTOZONE_REQUEST_STATUS.DEFAULT);
    setAutozonesData(null);
    setFieldOperationsData(null);
    mapInstance && removeAutoZonesLayer(mapInstance);
    mapInstance && removeSoilImageLayer(mapInstance);
    mapInstance && removeFieldOperationsLayer(mapInstance);
    setSoilOperationsData(null);
    setSelectedLayer(null);
  };
  const layerSelectionClassName = `${selectedLayer ?? ""}-open`;

  useLayoutEffect(() => {
    setSelectedLayer(null);
    setIsExpanded(false);
    setAutozoneRequestStatus(AUTOZONE_REQUEST_STATUS.DEFAULT);
    setSoilOperationsData(null);
    setAutozonesData(null);
    setFieldOperationsData(null);
    setAutozoneColorPalette(AutozoneColorPalette.DEFAULT);
    mapInstance && removeAutoZonesLayer(mapInstance);
    mapInstance && removeSoilImageLayer(mapInstance);
    mapInstance && removeFieldOperationsLayer(mapInstance);
  }, [
    steps,
    setAutozoneRequestStatus,
    setSoilOperationsData,
    setAutozonesData,
    setAutozoneColorPalette,
  ]);

  const resetAutozoneErrorStatus = (): void => {
    if (autozoneRequestStatus === AUTOZONE_REQUEST_STATUS.ERROR) {
      setAutozoneRequestStatus(AUTOZONE_REQUEST_STATUS.DEFAULT);
    }
  };

  const toggleDrawer = (): void => {
    if (isExpanded) {
      resetAutozoneErrorStatus();
    }
    setIsExpanded(!isExpanded);
    setSelectedLayer(null);
    hideDrawer();
  };
  const [selectedSoilL, setselectedSoilL] = useState<LayerSubtype[]>([]);
  const handleOverlayClick = (type: OverlayType): void => {
    if (type === "layers") {
      setActiveOverlay(type);
    }
  };

  const handleSubtypeClick = (subtype: LayerSubtype): void => {
    if (selectedSubtypes.includes(subtype)) {
      // setSelectedSubtypes(selectedSubtypes.filter(st => st !== subtype));
    } else {
      setSelectedSubtypes([...selectedSubtypes, subtype]);
      // Reset autozoneOpacity to default when re-selecting autozones
      if (subtype === "subtype1") {
        setAutozoneOpacity(60);
      }
    }
  };

  const handleSoilLClick = (subtype: LayerSoilL): void => {
    if (selectedSoilL.includes(subtype)) {
      // setSelectedSubtypes(selectedSubtypes.filter(st => st !== subtype));
    } else {
      setselectedSoilL([...selectedSoilL, subtype]);
    }
  };

  // Function to add soil image layer to the map
  return (
    <>
      <div
        className={`drawer${
          isExpanded ? " expanded" : ""
        } ${layerSelectionClassName}`}
      >
        <div style={{ width: "100%" }} className="drawer-header">
          <button
            style={{ width: "100%" }}
            className="toggle-button"
            onClick={toggleDrawer}
          >
            {isExpanded ? (
              <Flex justify="space-between" align="center" gap={4}>
                {formatTranslation(t("newTrial.trialMap.hide"))}
                <span>
                  <LineOutlined style={{ fontSize: "14px" }} />
                </span>
              </Flex>
            ) : (
              <Flex justify="space-between" align="center" gap={4}>
                {formatTranslation(t("newTrial.trialMap.expand"))}{" "}
                <span className="expand-icon">
                  <ExpandIcon />
                </span>
              </Flex>
            )}
          </button>
        </div>
        {isExpanded ? (
          <>
            {" "}
            <div
              className={`drawer-content ${
                isExpanded ? "drawer-expanded" : ""
              }`}
            >
              <div className="overlay-types">
                <NavLayerBtn
                  className={`overlay-button ${
                    activeOverlay === "layers" ? "active" : ""
                  }`}
                  onClick={() => {
                    handleOverlayClick("layers");
                  }}
                />
                {activeOverlay === "layers" && (
                  <div className="subtype-container">
                    <Flex gap={4} align="center" justify="center">
                      <div
                        className={`soilype-button ${
                          selectedLayer === LayerOption.Soil ? "selected" : ""
                        }`}
                        onClick={() => {
                          handleSoilLClick("subtype2");
                        }}
                      >
                        <SoilButtonComponent
                          onOpen={toggleDrawerLayer}
                          onClose={hideDrawer}
                          // onClearSelection={toggleDrawerLayer}
                          isSelected={selectedLayer === LayerOption.Soil}
                        />
                      </div>
                    </Flex>
                    <Flex
                      vertical={true}
                      gap={4}
                      align="center"
                      justify="center"
                    >
                      <div
                        className={`subtype-button ${
                          selectedLayer === LayerOption.Autozone
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          handleSubtypeClick("subtype1");
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <AutozonesComponent
                            onOpen={toggleDrawerLayer}
                            onClose={hideDrawer}
                            // onClearSelection={handleClearAutozoneSelection}
                            isSelected={selectedLayer === LayerOption.Autozone}
                          />
                        </div>
                      </div>
                    </Flex>
                    <Flex
                      vertical={true}
                      gap={4}
                      align="center"
                      justify="center"
                    >
                      <div
                        className={`subtype-button ${
                          selectedLayer === LayerOption.Machinery
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          handleSubtypeClick("subtype2");
                        }}
                      >
                        <MachineryButtonComponent
                          onOpen={toggleDrawerLayer}
                          onClose={hideDrawer}
                          // onClearSelection={handleClearMachinerySelection}
                          isSelected={selectedLayer === LayerOption.Machinery}
                        />
                      </div>
                    </Flex>
                  </div>
                )}
              </div>
              {/* <InfoCircleOutlined className="info-icon-soil" /> */}
            </div>
          </>
        ) : (
          <Flex vertical={true} gap={4} align="center" justify="center">
            <div>
              {activeOverlay === "layers" && <MapLayerMenu />}
              {activeOverlay === "satellite" && (
                <img
                  src={satelliteIcon}
                  alt={formatTranslation(
                    t("newTrial.mapControls.baseLayers.satellite")
                  )}
                />
              )}
              {activeOverlay === "agriculture" && (
                <img
                  src={agriIcon}
                  alt={formatTranslation(
                    t("newTrial.trialMap.overlayAgriculture")
                  )}
                />
              )}
              {activeOverlay === "map" && (
                <img
                  src={mapsIcon}
                  alt={formatTranslation(t("newTrial.trialMap.overlayMap"))}
                />
              )}
            </div>
            <Flex vertical={true} gap={4} align="center" justify="center">
              {selectedLayer === LayerOption.Autozone && (
                <div className="subtype-button">
                  <AutozonesComponent
                    onOpen={toggleDrawerLayer}
                    onClose={hideDrawer}
                    // onClearSelection={handleClearAutozoneSelection}
                    isSelected={selectedLayer === LayerOption.Autozone}
                  />
                </div>
              )}
              {selectedLayer === LayerOption.Machinery && (
                <div className="subtype-button">
                  <MachineryButtonComponent
                    onOpen={toggleDrawerLayer}
                    onClose={hideDrawer}
                    // onClearSelection={handleClearMachinerySelection}
                    isSelected={selectedLayer === LayerOption.Machinery}
                  />
                </div>
              )}
            </Flex>
          </Flex>
        )}
      </div>
    </>
  );
};

export default MapLayersDrawer;
